import {
	Box,
	Button,
	ColorMode,
	ModuleAppIcon,
	ThemeSpaceVariable,
	showToast,
	useColorMode,
} from "@caspeco/casper-ui-library";
import { type JSX, useEffect, useState } from "react";
import { Version } from "./models/version";
import { SplashScreen } from "./components/splash-screen";
import EmptyState from "./components/empty-state";
import { useTranslation } from "react-i18next";
import { useQuery } from "@tanstack/react-query";
import { getValueFromApp, haveAppFunction, isApp as isAppFunc, launchApp } from "./utils/app-helper";
import { isAndroid, isIOS } from "react-device-detect";
import { AppContext } from "./context/app-context";
import { ISafeAreaModel, SafeAreaModel } from "./models/safe-area-model";
import queryClient from "./query-client";

const MINIMUM_VERSION = new Version("1.1.0");

export const AppManager = (props: { children: JSX.Element }) => {
	const { t } = useTranslation();
	const { setColorMode } = useColorMode();
	const [versionStaus, setVersionStatus] = useState({
		haveCheckedVersion: false,
		versionOk: false,
		version: undefined as Version | undefined,
	});

	const [isApp, setIsApp] = useState(false);

	const { data: version, isLoading } = useQuery({
		queryKey: ["getApplicationVersion"],
		queryFn: () => getValueFromApp("getApplicationVersion", undefined, { timeout: 1000 }),
		select(data) {
			return new Version(data);
		},
		enabled: isApp,
		retry: 2,
	});

	const { data: theme, isLoading: themeIsLoading } = useQuery({
		queryKey: ["getAppTheme"],
		queryFn: () => getValueFromApp("getAppTheme", undefined, { timeout: 1000 }),
		enabled: isApp,
		retry: false,
	});

	const { data: safeArea, isLoading: safeAreaIsLoading } = useQuery({
		queryKey: ["getSafeAreaValues"],
		queryFn: () => getValueFromApp<ISafeAreaModel>("getSafeAreaValues", undefined, { timeout: 1000 }),
		select: (safeArea: ISafeAreaModel) => {
			return new SafeAreaModel(safeArea);
		},
		enabled: isApp,
		retry: false,
	});

	useEffect(() => {
		if (!themeIsLoading && theme) {
			setColorMode(theme?.toLowerCase() as ColorMode);
		}
	}, [setColorMode, theme, themeIsLoading]);

	useEffect(() => {
		const onThemeChanged = () => {
			queryClient.invalidateQueries({ queryKey: ["getAppTheme"] });
		};

		window.addEventListener("theme_changed", onThemeChanged);

		return () => window.removeEventListener("theme_changed", onThemeChanged);
	}, []);

	useEffect(() => {
		setTimeout(() => {
			const isApp = isAppFunc();
			setIsApp(isApp);

			if (!isApp) {
				setVersionStatus({ haveCheckedVersion: true, versionOk: true, version: undefined });
			}
		});

		const showErrorToast = () => {
			showToast({ description: t("error_toast_title"), type: "error" });
		};

		window.addEventListener("app_error", showErrorToast);
		return () => window.removeEventListener("app_error", showErrorToast);
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	useEffect(() => {
		if (!isLoading && isApp && !versionStaus.haveCheckedVersion) {
			const versionOk = version?.isGreterOrEqualToo(MINIMUM_VERSION) ?? false;
			setVersionStatus({ haveCheckedVersion: true, versionOk, version });
		}
	}, [version, isLoading, isApp, versionStaus.haveCheckedVersion]);

	if (!versionStaus.haveCheckedVersion || isLoading || safeAreaIsLoading) {
		return <SplashScreen />;
	} else if (versionStaus.versionOk) {
		return (
			<AppContext.Provider
				value={{ version: versionStaus.version, safeArea: safeArea ? safeArea : new SafeAreaModel() }}
			>
				{props.children}
			</AppContext.Provider>
		);
	} else {
		return (
			<Box
				h={"100vh"}
				px={ThemeSpaceVariable.Large}
				pt={safeArea?.topAsPx(ThemeSpaceVariable.Large)}
				pb={safeArea?.bottomAsPx(ThemeSpaceVariable.Large)}
			>
				<EmptyState
					icon={ModuleAppIcon}
					title={t("version_must_update")}
					text={t("version_must_update_info")}
					buttons={[
						<Button
							variant={"primary"}
							size={"lg"}
							key={"update"}
							onClick={() => {
								if (haveAppFunction("launchApp")) {
									if (isAndroid) {
										launchApp(["https://play.google.com/store/apps/details?id=se.caspeco.mypages"]);
									} else if (isIOS) {
										launchApp(["https://apps.apple.com/gb/app/caspeco_my_pages/id6742499315"]);
									}
								}
							}}
						>
							{t("choice_update")}
						</Button>,
					]}
				/>
			</Box>
		);
	}
};
